/**
 * Next.js reports `ChunkLoadError` when a deployment rotates hashed chunks
 * (or a crawler/bot never finishes the download). The first paint then fails
 * to hydrate. One reload per tab session is enough to pick up the new HTML
 * + chunk map; a second failure in the same session is left alone so a
 * permanently missing chunk cannot loop.
 */

export const CHUNK_LOAD_RELOAD_KEY = "braiv_chunk_load_reload";

export function isChunkLoadErrorMessage(message: string | null | undefined): boolean {
    if (!message) return false;
    const normalized = message.toLowerCase();
    return (
        normalized.includes("chunkloaderror") ||
        (normalized.includes("loading chunk") && (
            normalized.includes("failed") ||
            normalized.includes("timeout")
        )) ||
        normalized.includes("loading css chunk")
    );
}

export function shouldReloadAfterChunkLoadError(options: {
    message: string | null | undefined;
    alreadyReloaded: boolean;
}): boolean {
    return isChunkLoadErrorMessage(options.message) && !options.alreadyReloaded;
}

export function readChunkLoadReloadFlag(storage: Pick<Storage, "getItem"> | null | undefined): boolean {
    if (!storage) return false;
    try {
        return storage.getItem(CHUNK_LOAD_RELOAD_KEY) === "1";
    } catch {
        return false;
    }
}

export function writeChunkLoadReloadFlag(storage: Pick<Storage, "setItem"> | null | undefined): boolean {
    if (!storage) return false;
    try {
        storage.setItem(CHUNK_LOAD_RELOAD_KEY, "1");
        return true;
    } catch {
        return false;
    }
}

export function installChunkLoadRecovery(options?: {
    storage?: Pick<Storage, "getItem" | "setItem"> | null;
    reload?: () => void;
}): void {
    if (typeof window === "undefined") return;

    const storage = options?.storage === undefined
        ? (() => {
            try {
                return window.sessionStorage;
            } catch {
                return null;
            }
        })()
        : options.storage;
    const reload = options?.reload || (() => {
        window.location.reload();
    });

    const maybeReload = (message: string) => {
        if (!shouldReloadAfterChunkLoadError({
            message,
            alreadyReloaded: readChunkLoadReloadFlag(storage),
        })) {
            return;
        }
        writeChunkLoadReloadFlag(storage);
        reload();
    };

    window.addEventListener("error", (event) => {
        const errorMessage = event.error instanceof Error ? event.error.message : "";
        maybeReload(`${event.message || ""} ${errorMessage}`);
    });
    window.addEventListener("unhandledrejection", (event) => {
        const reason = event.reason;
        const message = reason instanceof Error
            ? `${reason.name} ${reason.message}`
            : String(reason || "");
        maybeReload(message);
    });
}
